import React, { useState, useEffect } from "react";
import "./Forgot-Password.scss";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FiEye, FiEyeOff, FiArrowLeft } from "react-icons/fi";
import { ToastContainer, Zoom } from "react-toastify";
import AuthLayout from "./Auth-Layout";
import {
  forgotPassword,
  resetPassword,
  verifyOtp,
} from "../../redux/slices/sliceActions";
import customToasts from "../assets/reusableComponents/myToasts";

const ForgotPassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { message, loading } = useSelector((state) => state.landlordslice);

  const [phone, setPhone] = useState("");
  const [userName, setUserName] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [step, setStep] = useState("phone"); // 'phone' | 'otp' | 'reset'
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);
  const [passError, setPassError] = useState("");
  const [values, setValues] = useState({
    code: "",
    password: "",
    confirm_pass: "",
  });

  // Kenyan phone number validation
  const validateKenyanPhone = (phoneNumber) => {
    const kenyanPhoneRegex = /^(07|01)\d{8}$/;
    return kenyanPhoneRegex.test(phoneNumber);
  };

  const handlePhoneChange = (e) => {
    const val = e.target.value;
    setPhone(val);
    if (val && !validateKenyanPhone(val)) {
      setPhoneError("Phone number must be in format 07XXXXXXXX or 01XXXXXXXX");
    } else {
      setPhoneError("");
    }
  };

  const handleValueChange = (e) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));

    if (name === "confirm_pass" || name === "password") {
      const otherPass = name === "password" ? values.confirm_pass : values.password;
      if (otherPass && value !== otherPass) {
        setPassError("Passwords do not match");
      } else {
        setPassError("");
      }
    }
  };

  // Step 1: Send reset code
  const handleSendCode = (e) => {
    e.preventDefault();
    const trimmed = phone.trim();
    if (!validateKenyanPhone(trimmed)) {
      setPhoneError("Phone number must be in format 07XXXXXXXX or 01XXXXXXXX");
      return;
    }

    customToasts
      .promise(
        dispatch(
          forgotPassword({ phone: `+254${trimmed.substring(1)}` })
        ).unwrap(),
        {
          pending: "Sending reset code...",
          success: "Verification code sent!",
          error: "Failed to send code",
        }
      )
      .then((res) => {
        setUserName(res?.data?.name || "");
        setStep("otp");
      })
      .catch((err) => {
        console.error("Forgot password error:", err);
      });
  };

  // Step 2: Verify OTP
  const handleVerifyOTP = (e) => {
    e.preventDefault();
    if (!values.code.trim()) {
      customToasts.error("Please enter the verification code");
      return;
    }

    customToasts
      .promise(
        dispatch(
          verifyOtp({
            phone: `+254${phone.trim().substring(1)}`,
            otp: values.code.trim(),
          })
        ).unwrap(),
        {
          pending: "Verifying code...",
          success: "Code verified successfully!",
          error: "Invalid verification code",
        }
      )
      .then(() => {
        setStep("reset");
      })
      .catch((err) => {
        console.error("OTP verification error:", err);
      });
  };

  // Step 3: Reset Password
  const handleResetPassword = (e) => {
    e.preventDefault();
    if (!values.password) {
      customToasts.error("Please enter a new password");
      return;
    }

    if (values.password !== values.confirm_pass) {
      setPassError("Passwords do not match");
      return;
    }

    customToasts
      .promise(
        dispatch(
          resetPassword({
            newPassword: values.password,
            phone: `+254${phone.trim().substring(1)}`,
          })
        ).unwrap(),
        {
          pending: "Resetting password...",
          success: "Password reset successful!",
          error: "Failed to reset password",
        }
      )
      .catch((err) => {
        console.error("Reset password error:", err);
      });
  };

  // Navigate to sign in when reset succeeds
  useEffect(() => {
    if (!loading && message === "reset success") {
      navigate("/signin");
    }
  }, [message, loading, navigate]);

  const getHeaderTitle = () => {
    if (step === "phone") return "Reset Password";
    if (step === "otp") return "Enter Code";
    return "New Password";
  };

  const getHeaderSubtitle = () => {
    if (step === "phone") return "Remember your credentials?";
    if (step === "otp") return "Didn't receive a code?";
    return "Ready to log in?";
  };

  return (
    <AuthLayout
      title={getHeaderTitle()}
      subtitle={getHeaderSubtitle()}
      subtitleLinkText="Sign in"
      subtitleLinkTo="/signin"
    >
      <div className="forgot-password-flow">
        {/* Step Indicators */}
        <div className="reset-step-indicator">
          <div className={`step-dot ${step === "phone" ? "active" : "completed"}`}>
            <span>1</span>
          </div>
          <div className={`step-line ${step !== "phone" ? "active" : ""}`} />
          <div
            className={`step-dot ${
              step === "otp" ? "active" : step === "reset" ? "completed" : ""
            }`}
          >
            <span>2</span>
          </div>
          <div className={`step-line ${step === "reset" ? "active" : ""}`} />
          <div className={`step-dot ${step === "reset" ? "active" : ""}`}>
            <span>3</span>
          </div>
        </div>

        {/* Step 1: Phone */}
        {step === "phone" && (
          <form className="forgot-form" onSubmit={handleSendCode} noValidate>
            <p className="flow-description">
              Enter your registered phone number. We'll send a verification code to help you reset your password.
            </p>

            <div className="form-group">
              <label className="input-label" htmlFor="forgot-phone">
                Phone Number
              </label>
              <div className="input-wrapper">
                <input
                  id="forgot-phone"
                  type="tel"
                  className={`auth-input ${phoneError ? "has-error" : ""}`}
                  placeholder="07XXXXXXXX or 01XXXXXXXX"
                  value={phone}
                  onChange={handlePhoneChange}
                  autoComplete="tel"
                  required
                />
              </div>
              {phoneError && (
                <span className="input-error-text">{phoneError}</span>
              )}
            </div>

            <button
              type="submit"
              className="auth-submit-btn"
              disabled={loading}
            >
              {loading ? <span className="btn-spinner" /> : "Send Reset Code"}
            </button>
          </form>
        )}

        {/* Step 2: OTP Verification */}
        {step === "otp" && (
          <form className="forgot-form" onSubmit={handleVerifyOTP} noValidate>
            <p className="flow-description">
              A verification code has been sent to{" "}
              <strong className="text-highlight">
                {phone.trim().substring(0, 4)}...{phone.trim().slice(-3)}
              </strong>
              {userName && (
                <>
                  {" "}
                  for user <strong className="text-highlight">{userName}</strong>
                </>
              )}
              . Please enter it below.
            </p>

            <div className="form-group">
              <label className="input-label" htmlFor="otp-code">
                Verification Code
              </label>
              <div className="input-wrapper">
                <input
                  id="otp-code"
                  name="code"
                  type="text"
                  inputMode="numeric"
                  className="auth-input otp-code-input"
                  placeholder="Enter 6-digit code"
                  value={values.code}
                  onChange={handleValueChange}
                  autoComplete="one-time-code"
                  required
                />
              </div>
            </div>

            <div className="btn-group-row">
              <button
                type="button"
                className="auth-back-btn"
                onClick={() => setStep("phone")}
              >
                <FiArrowLeft /> Back
              </button>

              <button
                type="submit"
                className="auth-submit-btn"
                disabled={loading || !values.code.trim()}
              >
                {loading ? <span className="btn-spinner" /> : "Verify Code"}
              </button>
            </div>
          </form>
        )}

        {/* Step 3: Set New Password */}
        {step === "reset" && (
          <form className="forgot-form" onSubmit={handleResetPassword} noValidate>
            <p className="flow-description">
              Create a new secure password for your RentlyBuzz account.
            </p>

            <div className="form-group">
              <label className="input-label" htmlFor="new-password">
                New Password
              </label>
              <div className="input-wrapper">
                <input
                  id="new-password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  className="auth-input has-toggle"
                  placeholder="Enter new password"
                  value={values.password}
                  onChange={handleValueChange}
                  autoComplete="new-password"
                  required
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label className="input-label" htmlFor="confirm-new-password">
                Confirm New Password
              </label>
              <div className="input-wrapper">
                <input
                  id="confirm-new-password"
                  name="confirm_pass"
                  type={showConfirmPass ? "text" : "password"}
                  className={`auth-input has-toggle ${passError ? "has-error" : ""}`}
                  placeholder="Confirm new password"
                  value={values.confirm_pass}
                  onChange={handleValueChange}
                  autoComplete="new-password"
                  required
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowConfirmPass((prev) => !prev)}
                  aria-label={
                    showConfirmPass ? "Hide password" : "Show password"
                  }
                >
                  {showConfirmPass ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
              {passError && (
                <span className="input-error-text">{passError}</span>
              )}
            </div>

            <button
              type="submit"
              className="auth-submit-btn"
              disabled={loading}
            >
              {loading ? <span className="btn-spinner" /> : "Reset Password"}
            </button>
          </form>
        )}

        <div className="back-login-footer">
          <Link to="/signin" className="back-login-link">
            <FiArrowLeft className="link-icon" />
            <span>Return to login</span>
          </Link>
        </div>
      </div>

      <ToastContainer
        position="bottom-left"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
        transition={Zoom}
      />
    </AuthLayout>
  );
};

export default ForgotPassword;
